import { Component, DestroyRef, inject, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { ActivatedRoute } from "@angular/router";
import { ConfirmSignUpComponent } from "./components/confirm-sign-up/confirm-sign-up.component";
import { ForgotPasswordComponent } from "./components/forgot-password/forgot-password.component";
import { SignInComponent } from "./components/sign-in/sign-in.component";
import { SignUpComponent } from "./components/sign-up/sign-up.component";
import {
  AuthenticateErrorEnum,
  AuthenticateStateEnum,
  SignInEnum,
} from "./enums/authenticate.enum";
import { ResetPasswordComponent } from "./components/reset-password/reset-password.component";
import { AuthService } from "./services/auth.service";
import { CognitoSignInResult } from "./interfaces/auth.interfaces";
import { LoaderService } from "../shared/services/loader.service";

/**
 * Authentication component that handles login, signup, password reset flows.
 * Only rendered when user is NOT authenticated (via canMatch guard in routes).
 */
@Component({
  selector: "app-auth",
  standalone: true,
  imports: [
    SignInComponent,
    SignUpComponent,
    ConfirmSignUpComponent,
    ForgotPasswordComponent,
    ResetPasswordComponent,
  ],
  templateUrl: "./auth.component.html",
})
export class AuthComponent implements OnInit {
  private readonly _authService = inject(AuthService);
  private readonly _destroyRef = inject(DestroyRef);
  private readonly _loaderService = inject(LoaderService);
  private readonly _route = inject(ActivatedRoute);

  protected readonly authenticateState = AuthenticateStateEnum;
  protected readonly state = signal(AuthenticateStateEnum.SIGN_IN);

  /**
   * On init, check for initial state from route data (e.g., /trial route).
   * Also check for pending credentials from trial registration.
   * If found, silently attempt login which will trigger OTP flow for UNCONFIRMED users.
   */
  ngOnInit(): void {
    // Check initial state from route data (e.g., /trial route starts with SIGN_UP)
    const initialState = this._route.snapshot.data["initialState"];
    if (initialState === "SIGN_UP") {
      this.state.set(AuthenticateStateEnum.SIGN_UP);
    }

    // Check for pending credentials from trial registration
    const pending = this._authService.pendingCredentials;
    if (pending) {
      this._autoLoginWithPendingCredentials(pending.email, pending.password);
    }
  }

  /**
   * Silently attempts login with pending credentials.
   * Shows loader to hide the login form during the process.
   * For UNCONFIRMED users, this will fail with UserNotConfirmedException,
   * which triggers the OTP confirmation screen.
   */
  private _autoLoginWithPendingCredentials(
    email: string,
    password: string,
  ): void {
    this._loaderService.show();

    this._authService
      .signIn(email, password)
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (result: CognitoSignInResult) => {
          this._loaderService.hide();
          // Unlikely for UNCONFIRMED users, but handle success
          if (result.type === SignInEnum.NEW_PASSWORD_REQUIRED) {
            this.state.set(AuthenticateStateEnum.RESET_PASSWORD);
          }
          // SUCCESS case is handled by auth guard redirect
        },
        error: (error: CognitoSignInResult) => {
          this._loaderService.hide();
          const result = error.result;
          if (
            result instanceof Error &&
            result.name ===
              (AuthenticateErrorEnum.USER_NOT_CONFIRMED_EXCEPTION as string)
          ) {
            // Expected for trial users: show OTP screen
            this.state.set(AuthenticateStateEnum.CONFIRM_SIGN_UP);
          }
          // Other errors: stay on sign-in (user can retry manually)
        },
      });
  }

  protected changeAuthState(event: AuthenticateStateEnum): void {
    this.state.set(event);
  }
}
